import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useProblemAnswers } from "../..";
import { getTemplateProblems } from "../../api/questionsApi";
import { addNewProblem, addProblemFromExisting, updateTemplateProblemAnswers } from "../../api/reportsApi";
import { successToast } from "../../helpers/successToast";
import { ProblemsDictionary } from "../../types/Metadata";
import { ProblemAnswer, ProblemType } from "../../types/Rows";
import { ProblemsHeader } from "./ProblemsHeader";
import { ProblemsListEditable } from "./problemsList";
import { v4 as uuidv4 } from "uuid";

export const ProblemsSection = ({
  items,
  reportId,
  problemType,
}: {
  items: ProblemAnswer[];
  templateId: string; // (kept if used elsewhere)
  reportId: string;
  problemType: ProblemType;
}) => {
  const queryClient = useQueryClient();
  const { data } = useQuery(
    ["ReportProblemsDictionary"],
    getTemplateProblems,
    { initialData: { categories: [], descriptions: [] } as ProblemsDictionary }
  );

  const { problemAnswers } = useProblemAnswers();

  const { mutateAsync: saveProblemsAnswers, isSuccess: isSaveProblemSuccess } =
    useMutation(
      async (problemAnswers: ProblemAnswer[]) => {
        if (problemAnswers.length > 0)
          return updateTemplateProblemAnswers(
            reportId!,
            problemAnswers,
            uuidv4()
          );
      },
      {
        onSuccess: () => {
          queryClient.resetQueries([reportId, "Problems"]);
        },
      }
    );

  const { mutate: addProblemTypeToDictionary } = useMutation(
    async ({ reportId, value }: { reportId: string; value: string }) =>
      addNewProblem(reportId, value),
    {
      onSuccess: () => {
        queryClient.resetQueries([reportId, "Problems"]);
        successToast();
      },
    }
  );

  const { mutate: addProblemQuestions } = useMutation(
    async ({
      reportId,
      categoryId,
    }: {
      reportId: string;
      categoryId: string;
    }) => addProblemFromExisting(reportId, categoryId, problemType),
    {
      onSuccess: () => {
        queryClient.resetQueries([reportId, "Problems"]);
        successToast();
      },
    }
  );

  const title =
    problemType === ProblemType.MAJOR
      ? "Lista poważnych znalezisk"
      : "Lista znalezisk";

  return (
    <div>
      <h2 className="text-[15px] font-semibold text-[var(--sc-text)] mb-3">{title}</h2>
      <div className="rounded-[10px] border border-[var(--sc-border)] overflow-hidden">
        <ProblemsHeader saveProblemsAnswers={saveProblemsAnswers} problemAnswers={problemAnswers} addProblemQuestions={addProblemQuestions} addProblemTypeToDictionary={addProblemTypeToDictionary} reportId={reportId} />
        <ProblemsListEditable items={items} reportId={reportId} />
      </div>
    </div>
  );
};